/*
 * validation.js — the fluent validation builder used by the entity field-type
 * registry (the recovered `v`/`e`, `email()`/`phone()`/`decimalPrecision()`,
 * and the `location` validator).
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (the .and()/.or() chain shape, e.string/e.numeric/e.integer/e.array,
 * e.length(min,max), e.between(a,b), e.equal(x), and the email/phone/
 * decimalPrecision/location factories are exactly what entity-field-types.js
 * calls), internal impl re-authored.
 *
 * A Rule is an immutable predicate tree. `run(value)` returns
 * { ok:boolean, message:string|null }. Combinators `.and()` / `.or()` return a
 * new Rule. `validate(value)` returns `true` on pass or the failing message
 * (Vuetify-rule convention). `toRules()` adapts to a Vuetify `:rules` array.
 */

function ok() { return { ok: true, message: null }; }
function fail(message) { return { ok: false, message: message || 'invalid' }; }

/** Default (English) messages; real app renders i18n keys via $t at the widget. */
export const MESSAGES = {
  string: 'Must be text',
  numeric: 'Must be a number',
  integer: 'Must be a whole number',
  array: 'Must be a list',
  length: 'Wrong length',
  between: 'Out of range',
  equal: 'Not allowed',
  email: 'Invalid e-mail',
  phone: 'Invalid phone number',
  decimal: 'Too many digits',
  location: 'Invalid location',
  required: 'Required',
};

export class Rule {
  constructor(runFn) {
    this._run = runFn;
  }

  run(value) {
    return this._run(value);
  }

  /** Passes only if BOTH pass; reports the first failure. */
  and(other) {
    const left = this;
    return new Rule((value) => {
      const a = left.run(value);
      if (!a.ok) return a;
      return toRule(other).run(value);
    });
  }

  /** Passes if EITHER passes; reports the left failure otherwise. */
  or(other) {
    const left = this;
    return new Rule((value) => {
      const a = left.run(value);
      if (a.ok) return a;
      const b = toRule(other).run(value);
      return b.ok ? b : a;
    });
  }

  /** Vuetify-rule convention: `true` on pass, message string on fail. */
  validate(value) {
    const r = this.run(value);
    return r.ok ? true : (r.message || MESSAGES.required);
  }

  /** As a Vuetify `:rules` array. */
  toRules() {
    return [(value) => this.validate(value)];
  }
}

/** Coerce a raw predicate/function/Rule into a Rule. */
export function toRule(x) {
  if (x instanceof Rule) return x;
  if (typeof x === 'function') {
    return new Rule((value) => {
      const res = x(value);
      if (res === true || res == null) return ok();
      if (res === false) return fail();
      return fail(String(res));
    });
  }
  // treat a bare value as an equality rule
  return new Rule((value) => (value === x ? ok() : fail(MESSAGES.equal)));
}

function isEmptyish(value) {
  return value == null || value === '';
}

/* ------------------------------------------------------------------ *
 * `v` — the fluent builder (recovered alias `e`).  Chain starters are
 * getters so each use produces a fresh Rule (matching `e.string.and(...)`).
 * ------------------------------------------------------------------ */
export const v = {
  get string() {
    return new Rule((value) =>
      typeof value === 'string' ? ok() : fail(MESSAGES.string));
  },

  get numeric() {
    return new Rule((value) => {
      if (typeof value === 'number' && !Number.isNaN(value)) return ok();
      if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return ok();
      return fail(MESSAGES.numeric);
    });
  },

  get integer() {
    return new Rule((value) => {
      const n = typeof value === 'string' ? Number(value) : value;
      return Number.isInteger(n) ? ok() : fail(MESSAGES.integer);
    });
  },

  get array() {
    return new Rule((value) => (Array.isArray(value) ? ok() : fail(MESSAGES.array)));
  },

  /** string length within [min,max] inclusive. */
  length(min, max) {
    return new Rule((value) => {
      const s = value == null ? '' : String(value);
      return s.length >= min && s.length <= max ? ok() : fail(MESSAGES.length);
    });
  },

  /** numeric value within [a,b] inclusive. */
  between(a, b) {
    return new Rule((value) => {
      const n = Number(value);
      return !Number.isNaN(n) && n >= a && n <= b ? ok() : fail(MESSAGES.between);
    });
  },

  /** strict equality (used as `.or(v.equal(null))` / `.or(v.equal(''))`). */
  equal(x) {
    return new Rule((value) => (value === x ? ok() : fail(MESSAGES.equal)));
  },
};

/* email() — recovered factory `i` (./index11.js). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function email() {
  return new Rule((value) => {
    if (isEmptyish(value)) return ok(); // emptiness handled by .or(equal(null|""))
    return EMAIL_RE.test(String(value)) ? ok() : fail(MESSAGES.email);
  });
}

/* phone() — recovered factory `g` (./index11.js). Lenient E.164-ish. */
const PHONE_RE = /^\+?[0-9][0-9\s\-()]{4,19}$/;
export function phone() {
  return new Rule((value) => {
    if (isEmptyish(value)) return ok();
    return PHONE_RE.test(String(value)) ? ok() : fail(MESSAGES.phone);
  });
}

/* decimalPrecision(intDigits, fracDigits) — recovered factory `h` (./index11.js).
 * Verifies the number fits N integer digits and M fractional digits, e.g.
 * decimalPrecision(12,6) for the `decimal` field type. */
export function decimalPrecision(intDigits, fracDigits) {
  return new Rule((value) => {
    if (isEmptyish(value)) return ok();
    const n = Number(value);
    if (Number.isNaN(n)) return fail(MESSAGES.numeric);
    const [ip, fp = ''] = String(Math.abs(n)).split('.');
    if (ip.replace(/^0+/, '').length > intDigits) return fail(MESSAGES.decimal);
    if (fp.length > fracDigits) return fail(MESSAGES.decimal);
    return ok();
  });
}

/* location validator — recovered `b` (./custom_validators.js). Accepts a
 * { lat, lng } (optionally { address, radius }) object or null. */
export const location = new Rule((value) => {
  if (value == null) return ok();
  const isNum = (x) => typeof x === 'number' && !Number.isNaN(x);
  if (typeof value === 'object' && isNum(value.lat) && isNum(value.lng)) return ok();
  return fail(MESSAGES.location);
});

/* `required` — ANDed onto a field's validation when field.required is true
 * (see model-factory.js). Empty string / null / empty array all fail. */
export const required = new Rule((value) => {
  if (value == null || value === '') return fail(MESSAGES.required);
  if (Array.isArray(value) && value.length === 0) return fail(MESSAGES.required);
  return ok();
});

export default { v, email, phone, decimalPrecision, location, required, Rule, toRule, MESSAGES };
